'use client';

import { useEffect, useId, useState } from 'react';
import Link from 'next/link';
import { Cookie, X } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { useConsent } from './consent-context';

export function CookieConsent() {
  const {
    state,
    hasDecided,
    ready,
    acceptAll,
    rejectAll,
    save,
    isOpen,
    openPreferences,
    closePreferences,
  } = useConsent();

  if (!ready) return null;

  const showBanner = !hasDecided;

  return (
    <>
      {showBanner && (
        <CookieBanner
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onCustomize={openPreferences}
        />
      )}
      {hasDecided && !isOpen && <ReopenPill onClick={openPreferences} />}
      {isOpen && (
        <PreferencesDialog
          state={state}
          onClose={closePreferences}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onSave={save}
        />
      )}
    </>
  );
}

function CookieBanner({
  onAcceptAll,
  onRejectAll,
  onCustomize,
}: {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onCustomize: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Aviso de cookies"
      className="fixed bottom-4 left-4 right-4 z-[100] sm:right-auto sm:max-w-md animate-fadeIn"
    >
      <div className="rounded-lg border border-border bg-white shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-3">
          <Cookie
            className="h-6 w-6 text-bluecaixa shrink-0 mt-0.5"
            aria-hidden
          />
          <h2 className="text-lg font-semibold text-bluecaixa">
            Controle sua privacidade
          </h2>
        </div>

        <p className="text-sm text-stone-700 leading-relaxed mb-3">
          Nosso site utiliza cookies para melhorar sua navegação, analisar o uso
          das páginas e oferecer conteúdo personalizado.
        </p>

        <div className="rounded-md border border-stone-200 bg-stone-50 p-3 mb-4">
          <p className="text-xs italic text-stone-600 leading-relaxed">
            Cookies de análise e publicidade só serão ativados após o seu
            consentimento. Cookies necessários ao funcionamento do site são
            sempre carregados.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm mb-4">
          <Link
            href="/aviso-de-privacidade"
            className="text-bluecaixa underline underline-offset-2 hover:text-primary-lightBlue"
          >
            Aviso de Privacidade
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onCustomize}
            className="text-sm font-medium text-bluecaixa underline underline-offset-2 hover:text-primary-lightBlue mr-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1 py-1"
          >
            Customizar
          </button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRejectAll}
            className="border-bluecaixa text-bluecaixa hover:bg-stone-50"
          >
            Rejeitar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onAcceptAll}
            className="bg-bluecaixa hover:bg-bluecaixa/90"
          >
            Aceitar
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReopenPill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Abrir preferências de cookies"
      className="fixed bottom-4 left-4 z-[90] flex h-12 w-12 items-center justify-center rounded-full bg-bluecaixa text-white shadow-lg hover:bg-bluecaixa/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Cookie className="h-5 w-5" aria-hidden />
    </button>
  );
}

type PreferencesDialogProps = {
  state: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
  };
  onClose: () => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: (partial: { analytics?: boolean; marketing?: boolean }) => void;
};

function PreferencesDialog({
  state,
  onClose,
  onAcceptAll,
  onRejectAll,
  onSave,
}: PreferencesDialogProps) {
  const [analytics, setAnalytics] = useState(state.analytics);
  const [marketing, setMarketing] = useState(state.marketing);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-stone-200 p-5">
          <div>
            <h2 id={titleId} className="text-xl font-semibold text-bluecaixa">
              Preferências de Cookies
            </h2>
            <p id={descId} className="mt-1 text-sm text-stone-600">
              Escolha quais cookies você permite que sejam utilizados.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-stone-500 hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <CategoryRow
            title="Cookies necessários"
            description="Essenciais para o funcionamento do site (segurança, navegação, sessão). Sempre ativos."
            checked
            disabled
            onChange={() => {}}
          />
          <CategoryRow
            title="Cookies de análise"
            description="Ajudam a entender como você usa o site (Microsoft Clarity, Google Tag Manager) para que possamos melhorar a experiência."
            checked={analytics}
            onChange={setAnalytics}
          />
          <CategoryRow
            title="Cookies de publicidade e personalização"
            description="Utilizados para medir a eficácia de campanhas e personalizar anúncios (Google Ads / Consent Mode)."
            checked={marketing}
            onChange={setMarketing}
          />
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-stone-200 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRejectAll}
              className="border-bluecaixa text-bluecaixa hover:bg-stone-50"
            >
              Rejeitar todos
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAcceptAll}
              className="border-bluecaixa text-bluecaixa hover:bg-stone-50"
            >
              Aceitar todos
            </Button>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => onSave({ analytics, marketing })}
            className="bg-bluecaixa hover:bg-bluecaixa/90"
          >
            Salvar preferências
          </Button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-stone-200 p-4">
      <div className="flex-1">
        <label
          htmlFor={id}
          className="block text-sm font-semibold text-stone-900"
        >
          {title}
        </label>
        <p className="mt-1 text-xs text-stone-600 leading-relaxed">
          {description}
        </p>
      </div>
      <Toggle
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        label={title}
      />
    </div>
  );
}

function Toggle({
  id,
  checked,
  disabled,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
        checked ? 'bg-bluecaixa' : 'bg-stone-300'
      }`}
    >
      <span
        aria-hidden
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
